import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import toast from 'react-hot-toast';
import {
  Send, Network, MessageSquare, Brain, Wrench, Activity,
  Settings, Trash2, Plus, ChevronRight, Bot, User, Clock,
  Zap, AlertTriangle, CheckCircle2, ArrowRight, Layers, X, Eye, Hash,
  Cpu, RotateCcw, DollarSign, BarChart3, Mic, MicOff, Volume2, VolumeX,
  Download, GitCompareArrows, ShieldCheck, ShieldAlert, Zap as ZapIcon,
  UserCircle2, Sparkles,
} from 'lucide-react';

// localStorage keys for LLM picker persistence
const LS_KEYS = {
  brainProvider: 'agent_brain_provider',
  brainModel:    'agent_brain_model',
  qaProvider:    'agent_qa_provider',
  qaModel:       'agent_qa_model',
};
const lsGet = (k) => { try { return localStorage.getItem(k) || ''; } catch { return ''; } };
const lsSet = (k, v) => { try { v ? localStorage.setItem(k, v) : localStorage.removeItem(k); } catch {} };
import { agentApi, instancesApi, llmApi } from '../api';
import useStore from '../store';
import {
  PageHeader, Card, Button, Badge, Input, Select, Spinner, EmptyState, ModalOverlay, StatCard,
} from '../components/UI';

const NODE_COLORS = {
  router:       { bg: 'rgba(99,102,241,0.18)',   bd: '#6366f1', fg: '#a5b4fc' },
  retriever:    { bg: 'rgba(6,182,212,0.18)',    bd: '#06b6d4', fg: '#67e8f9' },
  critic:       { bg: 'rgba(245,158,11,0.18)',   bd: '#f59e0b', fg: '#fcd34d' },
  synthesizer:  { bg: 'rgba(16,185,129,0.18)',   bd: '#10b981', fg: '#6ee7b7' },
  memoryWriter: { bg: 'rgba(168,85,247,0.18)',   bd: '#a855f7', fg: '#d8b4fe' },
  fallback:     { bg: 'rgba(239,68,68,0.18)',    bd: '#ef4444', fg: '#fca5a5' },
};

// ════════════════════════════════════════════════════════════════════════════
//  MAIN PAGE
// ════════════════════════════════════════════════════════════════════════════
export default function Agent() {
  const { t } = useTranslation(['agent', 'common']);
  const [tab, setTab] = useState('chat');
  const [lastRun, setLastRun] = useState(null); // Persist the most recent run for the Graph tab

  const tabs = [
    { id: 'chat',     icon: MessageSquare, label: t('tabs.chat') },
    { id: 'graph',    icon: Network,        label: t('tabs.graph') },
    { id: 'sessions', icon: Layers,         label: t('tabs.sessions') },
    { id: 'memory',   icon: Brain,          label: t('tabs.memory') },
    { id: 'tools',    icon: Wrench,         label: t('tabs.tools') },
    { id: 'traces',   icon: Activity,       label: t('tabs.traces') },
    { id: 'cost',     icon: DollarSign,     label: t('tabs.cost') },
    { id: 'eval',     icon: BarChart3,      label: t('tabs.eval') },
    { id: 'personas', icon: UserCircle2,    label: 'Personas' },
    { id: 'config',   icon: Settings,       label: t('tabs.config') },
  ];

  return (
    <div>
      <PageHeader title={t('title')} subtitle={t('subtitle')} />
      <div style={{ padding: '24px 36px', maxWidth: 1500, margin: '0 auto' }}>

      <div style={{
        display: 'flex', gap: 6, marginBottom: 24, flexWrap: 'wrap',
        padding: 6, background: 'var(--bg-2)', border: '1px solid var(--border)',
        borderRadius: 'var(--radius)', boxShadow: 'var(--shadow-sm)',
      }}>
        {tabs.map(({ id, icon: Icon, label }) => {
          const active = tab === id;
          return (
            <button
              key={id}
              onClick={() => setTab(id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '8px 14px', borderRadius: 'var(--radius-sm)',
                background: active
                  ? 'linear-gradient(135deg, var(--accent) 0%, var(--accent-dim) 100%)'
                  : 'transparent',
                border: '1px solid transparent',
                color: active ? '#fff' : 'var(--text-2)',
                cursor: 'pointer', fontFamily: 'var(--font-mono)', fontSize: 12,
                fontWeight: active ? 700 : 500,
                transition: 'all 0.2s ease',
                boxShadow: active ? '0 4px 14px var(--accent-glow-strong), inset 0 1px 0 rgba(255,255,255,0.15)' : 'none',
                position: 'relative',
              }}
              onMouseEnter={e => { if (!active) { e.currentTarget.style.background = 'var(--bg-3)'; e.currentTarget.style.color = 'var(--text-1)'; } }}
              onMouseLeave={e => { if (!active) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-2)'; } }}
            >
              <Icon size={14} />
              {label}
            </button>
          );
        })}
      </div>

      {tab === 'chat'     && <ChatTab onRun={setLastRun} />}
      {tab === 'graph'    && <GraphTab lastRun={lastRun} />}
      {tab === 'sessions' && <SessionsTab />}
      {tab === 'memory'   && <MemoryTab />}
      {tab === 'tools'    && <ToolsTab />}
      {tab === 'traces'   && <TracesTab />}
      {tab === 'cost'     && <CostTab />}
      {tab === 'eval'     && <EvalTab />}
      {tab === 'personas' && <PersonasTab />}
      {tab === 'config'   && <ConfigTab />}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  TAB: CHAT
// ════════════════════════════════════════════════════════════════════════════
function ChatTab({ onRun }) {
  const { t } = useTranslation(['agent', 'common']);
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  // Pre-populate restrict picker from query string (e.g. coming from a Tenant card).
  // Supported params: instanceIds=id1,id2,…  tenantId=…  tenantName=…
  const initialRestrict = (searchParams.get('instanceIds') || '')
    .split(',').map(s => s.trim()).filter(Boolean);
  const initialTenant   = searchParams.get('tenantId')
    ? { id: searchParams.get('tenantId'), name: searchParams.get('tenantName') || 'Tenant' }
    : null;

  const [sessionId, setSessionId] = useState(null);
  const [restrictIds, setRestrictIds] = useState(initialRestrict);
  const [tenantCtx, setTenantCtx] = useState(initialTenant);
  const [input, setInput] = useState('');
  const [history, setHistory] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [showLLM, setShowLLM] = useState(false);
  const [brainProvider, setBrainProvider] = useState(() => lsGet(LS_KEYS.brainProvider));
  const [brainModel,    setBrainModel]    = useState(() => lsGet(LS_KEYS.brainModel));
  const [qaProvider,    setQaProvider]    = useState(() => lsGet(LS_KEYS.qaProvider));
  const [qaModel,       setQaModel]       = useState(() => lsGet(LS_KEYS.qaModel));
  const scrollRef = useRef();

  // Persist LLM selections
  useEffect(() => lsSet(LS_KEYS.brainProvider, brainProvider), [brainProvider]);
  useEffect(() => lsSet(LS_KEYS.brainModel,    brainModel),    [brainModel]);
  useEffect(() => lsSet(LS_KEYS.qaProvider,    qaProvider),    [qaProvider]);
  useEffect(() => lsSet(LS_KEYS.qaModel,       qaModel),       [qaModel]);

  const { data: instances } = useQuery({ queryKey: ['instances'], queryFn: instancesApi.list });
  const { data: sessions } = useQuery({ queryKey: ['agent-sessions'], queryFn: agentApi.listSessions });
  const { data: providers } = useQuery({ queryKey: ['providers'], queryFn: llmApi.providers });

  // Auto-load messages when switching session
  useEffect(() => {
    if (!sessionId) { setHistory([]); return; }
    agentApi.getMessages(sessionId).then(r => {
      const msgs = (r.messages || []).map(m => ({
        role: m.role,
        content: m.content,
        citations: m.citations || [],
        routing:   m.routing || null,
        iterations: m.iterations,
        latencyMs:  m.latencyMs,
        timestamp:  m.createdAt,
      }));
      setHistory(msgs);
    }).catch(() => {});
  }, [sessionId]);

  const [streaming, setStreaming] = useState(false);
  const [liveSteps, setLiveSteps] = useState([]);
  const [streamingText, setStreamingText] = useState(''); // accumulator for token streaming
  const [showCompare, setShowCompare] = useState(false);
  const [personaId, setPersonaId] = useState(() => lsGet('agent_persona_id') || '');
  const abortRef = useRef(null);

  // Persist persona selection
  useEffect(() => lsSet('agent_persona_id', personaId), [personaId]);

  const { data: personasResp } = useQuery({ queryKey: ['agent-personas'], queryFn: agentApi.personas });
  const personas = personasResp?.personas || [];
  const activePersona = personas.find(p => p.id === personaId) || null;

  // Auto-prefill + auto-send from ?q=... (used by Dashboard quick-query bar).
  // Sets a flag; a separate effect actually fires `send()` once all
  // dependencies (instances, models) are loaded.
  const [pendingAutoSend, setPendingAutoSend] = useState(false);
  useEffect(() => {
    const q = searchParams.get('q');
    if (q && !input) {
      setInput(q);
      setPendingAutoSend(true);
    }
  }, []); // eslint-disable-line

  // Auto-scroll to bottom on new messages, streaming tokens, and step updates.
  // Use rAF so the DOM has painted the new content before we read scrollHeight.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    requestAnimationFrame(() => { el.scrollTop = el.scrollHeight; });
  }, [history, liveSteps.length, streamingText]);

  const newSessionMut = useMutation({
    mutationFn: () => agentApi.createSession({ title: 'Conversazione ' + new Date().toLocaleString() }),
    onSuccess: (s) => { setSessionId(s.id); setHistory([]); qc.invalidateQueries(['agent-sessions']); },
  });

  const send = async () => {
    if (!input.trim() || streaming) return;
    const question = input;
    // Push the user message immediately so the UI feels reactive
    setHistory(h => [...h, { role: 'user', content: question }]);
    setInput('');
    setLiveSteps([]);
    setStreamingText('');
    setStreaming(true);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      await agentApi.queryStream(
        {
          question,
          sessionId,
          instanceIds:   restrictIds.length > 0 ? restrictIds : undefined,
          tenantId:      tenantCtx?.id || undefined,
          personaId:     personaId || undefined,
          createSession: true,
          brainProvider: brainProvider || undefined,
          brainModel:    brainModel    || undefined,
          qaProvider:    qaProvider    || undefined,
          qaModel:       qaModel       || undefined,
        },
        {
          signal: controller.signal,
          onStep:  (step) => setLiveSteps(prev => [...prev, step]),
          onToken: ({ chunk }) => setStreamingText(prev => prev + chunk),
          onDone: (res) => {
            setHistory(h => [
              ...h,
              {
                role: 'assistant', content: res.answer || t('chat.noAnswer'),
                citations:    res.sources || [],
                routing:      res.routing,
                trace:        res.trace,
                iterations:   res.iterations,
                fallbackUsed: res.fallbackUsed,
                fromCache:    res.fromCache,
                citationsVerified: res.citationsVerified,
                rerankedCount: res.rerankedCount,
                toolCalls:    res.toolCalls,
                latencyMs:    res.latencyMs,
                cost:         res.cost,
                persona:      res.persona,
                customChecks: res.customChecks,
              },
            ]);
            if (res.sessionId && res.sessionId !== sessionId) {
              setSessionId(res.sessionId);
              qc.invalidateQueries(['agent-sessions']);
            }
            onRun?.(res);
          },
          onError: (err) => toast.error(err?.message || 'Errore agent'),
        }
      );
    } catch (err) {
      if (err.name !== 'AbortError') toast.error(err.message || 'Errore stream');
    } finally {
      setStreaming(false);
      setLiveSteps([]);
      setStreamingText('');
      abortRef.current = null;
    }
  };

  // Export session as Markdown
  const exportSession = async () => {
    if (!sessionId) return;
    try {
      const blob = await agentApi.exportSession(sessionId);
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url; a.download = `session-${sessionId.substring(0, 8)}.md`;
      a.click(); URL.revokeObjectURL(url);
    } catch (err) { toast.error('Export failed'); }
  };

  const cancel = () => {
    if (abortRef.current) abortRef.current.abort();
  };

  // Fire the pending auto-send (from Dashboard ?q=) once input is set and
  // dependencies are loaded. Single shot.
  useEffect(() => {
    if (!pendingAutoSend) return;
    if (!input.trim()) return;
    if (streaming) return;
    if (!instances) return; // wait for instances query
    setPendingAutoSend(false);
    // Clean ?q from URL now that we're submitting
    const np = new URLSearchParams(searchParams);
    np.delete('q');
    setSearchParams(np, { replace: true });
    // Fire the send
    send();
  }, [pendingAutoSend, input, streaming, instances]); // eslint-disable-line

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 260px) minmax(0, 1fr)', gap: 16, minHeight: 600 }}>
      {/* ── Sessions side panel ─────────────────────────────────────── */}
      <Card style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 700, overflowY: 'auto' }}>
        <Button icon={Plus} onClick={() => newSessionMut.mutate()} loading={newSessionMut.isPending}>
          {t('chat.newSession')}
        </Button>
        <div style={{ fontSize: 10, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '1px', marginTop: 8, fontWeight: 700 }}>
          {t('chat.currentSession')}
        </div>
        {(sessions?.sessions || []).map(s => (
          <div
            key={s.id}
            onClick={() => setSessionId(s.id)}
            style={{
              padding: 10, borderRadius: 'var(--radius-sm)',
              background: sessionId === s.id ? 'var(--accent-glow)' : 'var(--bg-3)',
              border: `1px solid ${sessionId === s.id ? 'var(--accent)' : 'var(--border)'}`,
              cursor: 'pointer', fontFamily: 'var(--font-mono)', fontSize: 11,
            }}
          >
            <div style={{ color: sessionId === s.id ? 'var(--accent)' : 'var(--text-1)', fontWeight: 600, marginBottom: 4 }}>
              {s.title || s.id.substring(0, 8)}
            </div>
            <div style={{ fontSize: 10, color: 'var(--text-3)' }}>
              {new Date(s.lastActiveAt).toLocaleString()}
            </div>
          </div>
        ))}
        {(!sessions?.sessions || sessions.sessions.length === 0) && (
          <div style={{ fontSize: 11, color: 'var(--text-3)', textAlign: 'center', padding: 12 }}>
            {t('chat.noSession')}
          </div>
        )}
      </Card>

      {/* ── Chat panel ─────────────────────────────────────────────── */}
      <Card style={{ padding: 0, display: 'flex', flexDirection: 'column', height: 700 }}>
        {/* Persona picker */}
        <div style={{
          padding: '10px 12px', borderBottom: '1px solid var(--border)',
          display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center',
        }}>
          <span style={{ fontSize: 11, color: 'var(--text-3)', fontFamily: 'var(--font-mono)', marginRight: 4 }}>
            Persona:
          </span>
          {personas.length === 0 ? (
            <span style={{ fontSize: 11, color: 'var(--text-3)', fontStyle: 'italic' }}>(caricamento…)</span>
          ) : (
            personas.map(p => {
              const sel = (personaId || personas.find(x => x.isDefault)?.id) === p.id;
              return (
                <button
                  key={p.id}
                  onClick={() => setPersonaId(sel ? '' : p.id)}
                  title={p.description || p.name}
                  style={{
                    padding: '4px 10px', borderRadius: 14, fontSize: 11,
                    background: sel ? `${p.color}25` : 'var(--bg-3)',
                    border: `1px solid ${sel ? p.color : 'var(--border)'}`,
                    color: sel ? p.color : 'var(--text-2)',
                    cursor: 'pointer', fontFamily: 'var(--font-mono)',
                    display: 'inline-flex', alignItems: 'center', gap: 5,
                    fontWeight: sel ? 700 : 500,
                    transition: 'all 0.2s',
                    boxShadow: sel ? `0 0 12px ${p.color}40` : 'none',
                  }}
                >
                  <span>{p.icon}</span>
                  <span>{p.name}</span>
                </button>
              );
            })
          )}
          {activePersona?.description && (
            <span style={{ fontSize: 10, color: 'var(--text-3)', marginLeft: 8, fontStyle: 'italic' }}>
              {activePersona.description}
            </span>
          )}
        </div>

        {/* LLM picker */}
        <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)' }}>
          <button
            onClick={() => setShowLLM(s => !s)}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              background: 'transparent', border: 'none', cursor: 'pointer',
              fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-2)', padding: 0,
            }}
          >
            <Cpu size={13} style={{ color: 'var(--accent2)' }} />
            <span style={{ fontWeight: 700 }}>{t('chat.models')}</span>
            <span style={{ color: 'var(--text-3)' }}>
              {brainProvider || brainModel || qaProvider || qaModel
                ? `[${brainProvider || 'def'}/${brainModel || 'def'} · ${qaProvider || 'def'}/${qaModel || 'def'}]`
                : `(${t('chat.useDefault')})`}
            </span>
            {showLLM ? <ChevronRight size={11} style={{ transform: 'rotate(90deg)' }} /> : <ChevronRight size={11} />}
          </button>
          {showLLM && (
            <LLMSelector
              providers={providers?.providers || []}
              brainProvider={brainProvider} setBrainProvider={setBrainProvider}
              brainModel={brainModel}       setBrainModel={setBrainModel}
              qaProvider={qaProvider}       setQaProvider={setQaProvider}
              qaModel={qaModel}             setQaModel={setQaModel}
              onReset={() => { setBrainProvider(''); setBrainModel(''); setQaProvider(''); setQaModel(''); }}
            />
          )}
        </div>
        {/* Restrict picker */}
        <div style={{ padding: 12, borderBottom: '1px solid var(--border)', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, color: 'var(--text-3)', fontFamily: 'var(--font-mono)' }}>
            {t('chat.restrictTo')}:
          </span>
          {(instances?.instances || []).map(i => {
            const sel = restrictIds.includes(i.id);
            return (
              <button
                key={i.id}
                onClick={() => setRestrictIds(s => sel ? s.filter(x => x !== i.id) : [...s, i.id])}
                style={{
                  padding: '3px 9px', borderRadius: 12,
                  background: sel ? 'rgba(6,182,212,0.18)' : 'var(--bg-3)',
                  border: `1px solid ${sel ? 'var(--accent2)' : 'var(--border)'}`,
                  color: sel ? 'var(--accent2)' : 'var(--text-2)',
                  cursor: 'pointer', fontSize: 11, fontFamily: 'var(--font-mono)',
                }}
              >
                {i.name}
              </button>
            );
          })}
          {restrictIds.length === 0 && (
            <span style={{ fontSize: 11, color: 'var(--text-3)', fontStyle: 'italic' }}>
              ({t('chat.allInstances')})
            </span>
          )}
          {tenantCtx && (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '3px 10px', fontSize: 11, fontFamily: 'var(--font-mono)',
              background: 'linear-gradient(135deg, rgba(244,114,182,0.15), rgba(139,92,246,0.10))',
              border: '1px solid rgba(244,114,182,0.4)', color: 'var(--accent3)',
              borderRadius: 12, fontWeight: 700,
            }}>
              🏢 {tenantCtx.name}
              <button
                onClick={() => {
                  setTenantCtx(null);
                  setRestrictIds([]);
                  const np = new URLSearchParams(searchParams);
                  np.delete('tenantId'); np.delete('tenantName'); np.delete('instanceIds');
                  setSearchParams(np);
                }}
                title="Esci dalla modalità tenant"
                style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', padding: 0, fontSize: 12, opacity: 0.7 }}
              >✕</button>
            </span>
          )}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
            <button
              onClick={() => setShowCompare(true)}
              title={t('compare.title')}
              style={iconBtnTop}
            ><GitCompareArrows size={13} /></button>
            <button
              onClick={exportSession}
              disabled={!sessionId}
              title={t('common.export')}
              style={{ ...iconBtnTop, opacity: sessionId ? 1 : 0.4 }}
            ><Download size={13} /></button>
          </div>
        </div>

        {/* Messages */}
        <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: 16, display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
          {history.length === 0 && (
            <EmptyState icon={Network} title={t('title')} description={t('chat.empty')} />
          )}
          {history.map((m, i) => (
            <ChatMessage
              key={i}
              msg={m}
              expanded={!!expanded[i]}
              onToggle={() => setExpanded(s => ({ ...s, [i]: !s[i] }))}
            />
          ))}
          {streaming && (
            <>
              <ThinkingIndicator steps={liveSteps} onCancel={cancel} />
              {streamingText && <StreamingMessage text={streamingText} />}
            </>
          )}
        </div>

        {/* Input */}
        <div style={{ padding: 12, borderTop: '1px solid var(--border)', display: 'flex', gap: 8 }}>
          <VoiceInput onTranscript={(text) => setInput(prev => prev ? prev + ' ' + text : text)} disabled={streaming} />
          <textarea
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder={t('chat.placeholder')}
            rows={2}
            style={{
              flex: 1, background: 'var(--bg-3)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)', color: 'var(--text-1)', padding: 10,
              fontSize: 13, resize: 'none', fontFamily: 'inherit',
            }}
          />
          <Button onClick={send} icon={Send} disabled={!input.trim() || streaming}>
            {t('chat.send')}
          </Button>
        </div>
      </Card>

      {showCompare && <CompareModal onClose={() => setShowCompare(false)} restrictIds={restrictIds} />}
    </div>
  );
}

function StreamingMessage({ text }) {
  return (
    <div className="animate-fade-up" style={{ display: 'flex', gap: 12 }}>
      <div style={{
        width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'linear-gradient(135deg, var(--accent) 0%, var(--accent2) 100%)',
        color: '#fff', boxShadow: '0 4px 12px var(--accent-glow-strong)',
      }}><Bot size={16} /></div>
      <div style={{ flex: 1, maxWidth: '85%', minWidth: 0 }}>
        <div style={{
          padding: '12px 16px', borderRadius: 14, borderTopLeftRadius: 4,
          background: 'var(--bg-3)', border: '1px solid var(--border-bright)',
          boxShadow: '0 0 20px var(--accent-glow)',
          fontSize: 13, lineHeight: 1.65, color: 'var(--text-1)',
          position: 'relative', minWidth: 0, overflow: 'hidden',
        }}>
          <div className="markdown-body">{renderWithVideos(text)}</div>
          <span style={{
            display: 'inline-block', width: 8, height: 14,
            background: 'var(--accent2)', marginLeft: 2,
            verticalAlign: 'middle', animation: 'pulse 1s ease-in-out infinite',
          }} />
        </div>
      </div>
    </div>
  );
}

function VoiceInput({ onTranscript, disabled }) {
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  async function start() {
    if (disabled || recording || busy) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setBusy(true);
        try {
          const form = new FormData();
          form.append('file', blob, 'recording.webm');
          const url = (process.env.REACT_APP_API_URL || '/api') + '/transcribe';
          const res = await fetch(url, { method: 'POST', credentials: 'include', body: form });
          const data = await res.json();
          if (data?.text) onTranscript(data.text);
        } catch (err) { toast.error('Trascrizione fallita'); }
        setBusy(false);
      };
      mr.start();
      mediaRef.current = mr;
      setRecording(true);
    } catch (err) { toast.error('Microfono non accessibile'); }
  }
  function stop() {
    if (mediaRef.current && recording) {
      mediaRef.current.stop();
      setRecording(false);
    }
  }
  return (
    <button
      onClick={recording ? stop : start}
      disabled={disabled || busy}
      title={recording ? 'Stop' : 'Voice input'}
      style={{
        width: 42, height: 42, borderRadius: 'var(--radius-sm)',
        background: recording
          ? 'linear-gradient(135deg, var(--red) 0%, #dc2626 100%)'
          : busy ? 'var(--bg-3)' : 'var(--bg-3)',
        border: `1px solid ${recording ? 'var(--red)' : 'var(--border)'}`,
        color: recording ? '#fff' : (busy ? 'var(--text-3)' : 'var(--text-2)'),
        cursor: disabled ? 'not-allowed' : 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: recording ? '0 0 16px rgba(248,113,113,0.5)' : 'none',
        animation: recording ? 'pulseDot 1.5s ease-in-out infinite' : 'none',
        flexShrink: 0,
      }}
    >
      {busy ? <Spinner size={16} /> : recording ? <MicOff size={16} /> : <Mic size={16} />}
    </button>
  );
}

function CompareModal({ onClose, restrictIds }) {
  const { t } = useTranslation(['agent']);
  const { data: providers } = useQuery({ queryKey: ['providers'], queryFn: () => require('../api').llmApi.providers() });
  const [question, setQuestion] = useState('');
  const [a, setA] = useState({ brainProvider: 'openai', brainModel: 'gpt-4o-mini', qaProvider: 'openai', qaModel: 'gpt-4o-mini' });
  const [b, setB] = useState({ brainProvider: 'openai', brainModel: 'gpt-4o',      qaProvider: 'openai', qaModel: 'gpt-4o' });
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!question.trim() || running) return;
    setRunning(true); setResult(null);
    try {
      const res = await agentApi.compare({
        question, instanceIds: restrictIds.length > 0 ? restrictIds : undefined,
        configA: a, configB: b,
      });
      setResult(res);
    } catch (err) { toast.error(err.message || 'Compare failed'); }
    setRunning(false);
  };

  return (
    <ModalOverlay onClose={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'var(--bg-2)', border: '1px solid var(--border-bright)', borderRadius: 'var(--radius)',
        padding: 24, width: 'min(1100px, 100%)', maxHeight: 'calc(100dvh - 40px)', overflowY: 'auto',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <div>
            <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 18 }}>{t('compare.title')}</h3>
            <p style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 4 }}>{t('compare.subtitle')}</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-3)', cursor: 'pointer' }}><X size={18} /></button>
        </div>

        <Input label="Domanda" value={question} onChange={e => setQuestion(e.target.value)} placeholder="Cosa è un digital twin?" />

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 12 }}>
          {[
            { label: t('compare.configA'), v: a, set: setA, color: 'var(--accent2)' },
            { label: t('compare.configB'), v: b, set: setB, color: 'var(--accent3)' },
          ].map(({ label, v, set, color }) => (
            <div key={label} style={{
              padding: 14, background: 'var(--bg-3)', border: `1px solid ${color}40`,
              borderRadius: 'var(--radius-sm)',
            }}>
              <div style={{ fontSize: 11, color, fontWeight: 700, letterSpacing: '1px', marginBottom: 8 }}>{label}</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 6 }}>
                <SmallSelect label={t('compare.brain') + ' P'} value={v.brainProvider} onChange={x => set({ ...v, brainProvider: x, brainModel: '' })}
                  options={[{ value: 'openai', label: 'OpenAI' }, { value: 'ollama', label: 'Ollama' }]} />
                <SmallSelect label={t('compare.brain') + ' M'} value={v.brainModel} onChange={x => set({ ...v, brainModel: x })}
                  options={(providers?.providers?.find(p => p.id === v.brainProvider)?.models || []).map(m => ({ value: m.id, label: m.id }))} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                <SmallSelect label={t('compare.synth') + ' P'} value={v.qaProvider} onChange={x => set({ ...v, qaProvider: x, qaModel: '' })}
                  options={[{ value: 'openai', label: 'OpenAI' }, { value: 'ollama', label: 'Ollama' }]} />
                <SmallSelect label={t('compare.synth') + ' M'} value={v.qaModel} onChange={x => set({ ...v, qaModel: x })}
                  options={(providers?.providers?.find(p => p.id === v.qaProvider)?.models || []).map(m => ({ value: m.id, label: m.id }))} />
              </div>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
          <Button onClick={run} loading={running} icon={GitCompareArrows} disabled={!question.trim()}>{t('compare.run')}</Button>
        </div>

        {result && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            {[
              { side: 'A', data: result.a, color: 'var(--accent2)' },
              { side: 'B', data: result.b, color: 'var(--accent3)' },
            ].map(({ side, data, color }) => (
              <div key={side} style={{
                padding: 14, background: 'var(--bg-3)', borderRadius: 'var(--radius-sm)',
                border: `1px solid ${color}40`,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <span style={{ fontSize: 16, fontWeight: 800, color }}>{side}</span>
                  <Badge color="cyan">{data.iterations} iter</Badge>
                  <Badge color="green">{data.latencyMs}ms</Badge>
                  <Badge color={data.fromCache ? 'yellow' : 'purple'}>
                    {data.fromCache ? 'cache' : `$${(data.cost?.usd || 0).toFixed(4)}`}
                  </Badge>
                </div>
                <div className="markdown-body" style={{ fontSize: 12, lineHeight: 1.5 }}>
                  <ReactMarkdown>{data.answer || '_(nessuna risposta)_'}</ReactMarkdown>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </ModalOverlay>
  );
}

const NODE_LABEL = {
  router:       { label: 'Routing',       icon: '🔀', color: '#a5b4fc' },
  retriever:    { label: 'Retrieval',     icon: '🔍', color: '#67e8f9' },
  critic:       { label: 'Critic',        icon: '🧐', color: '#fcd34d' },
  synthesizer:  { label: 'Synthesis',     icon: '✍️', color: '#6ee7b7' },
  memoryWriter: { label: 'Memory write',  icon: '💾', color: '#d8b4fe' },
  fallback:     { label: 'Fallback',      icon: '⚠️', color: '#fca5a5' },
};

function ThinkingIndicator({ steps = [], onCancel }) {
  const { t } = useTranslation(['agent']);
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const start = Date.now();
    const iv = setInterval(() => setElapsed(Math.floor((Date.now() - start) / 1000)), 250);
    return () => clearInterval(iv);
  }, []);

  // Build a status list from live SSE step events.
  // For each `start` event without matching `done`, the node is currently active.
  const rows = [];
  const startedAt = {}; // node|iter -> ts
  for (let i = 0; i < steps.length; i++) {
    const s = steps[i];
    const key = `${s.node}#${s.iteration ?? 0}`;
    if (s.phase === 'start') {
      startedAt[key] = i;
      rows.push({ node: s.node, status: 'running', durationMs: null, summary: null, _key: key });
    } else if (s.phase === 'done') {
      // Find the running row for this key and mark done
      const idx = rows.findIndex(r => r._key === key && r.status === 'running');
      if (idx >= 0) rows[idx] = { ...rows[idx], status: 'done', durationMs: s.durationMs, summary: s.summary, ok: s.ok };
      else rows.push({ node: s.node, status: 'done', durationMs: s.durationMs, summary: s.summary, ok: s.ok, _key: key });
    }
  }

  const currentRow = rows.find(r => r.status === 'running');

  return (
    <div style={{
      alignSelf: 'flex-start', minWidth: 360, maxWidth: 600,
      background: 'linear-gradient(135deg, rgba(139,92,246,0.06) 0%, rgba(34,211,238,0.04) 100%), var(--bg-2)',
      border: '1px solid var(--border-bright)',
      borderRadius: 'var(--radius)',
      padding: 14,
      boxShadow: 'var(--shadow-sm), 0 0 32px var(--accent-glow)',
      fontFamily: 'var(--font-mono)', fontSize: 12,
      animation: 'fadeIn 0.25s ease',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <Spinner size={14} />
        <span style={{ color: 'var(--text-1)', fontWeight: 700 }}>{t('chat.thinking')}</span>
        <span style={{ color: 'var(--accent2)', fontWeight: 700 }}>{elapsed}s</span>
        {currentRow && (
          <span style={{ marginLeft: 'auto', color: NODE_LABEL[currentRow.node]?.color, fontWeight: 700 }}>
            {NODE_LABEL[currentRow.node]?.icon} {NODE_LABEL[currentRow.node]?.label || currentRow.node}…
          </span>
        )}
        {onCancel && (
          <button onClick={onCancel} title="Stop"
            style={{
              background: 'transparent', border: '1px solid var(--border)',
              color: 'var(--text-3)', cursor: 'pointer',
              padding: '2px 8px', borderRadius: 4, fontSize: 10, marginLeft: currentRow ? 0 : 'auto',
            }}>stop</button>
        )}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {rows.map((r, i) => {
          const cfg = NODE_LABEL[r.node] || { label: r.node, icon: '·', color: 'var(--text-2)' };
          const isRun = r.status === 'running';
          const isErr = r.ok === false;
          return (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '5px 8px', borderRadius: 6,
              background: isRun ? 'rgba(255,255,255,0.03)' : 'transparent',
              borderLeft: `2px solid ${isRun ? cfg.color : 'transparent'}`,
              opacity: isRun ? 1 : (r.status === 'done' ? 0.85 : 0.5),
            }}>
              <span style={{ width: 16, textAlign: 'center' }}>
                {isRun
                  ? <span style={{ display: 'inline-block', animation: 'pulseDot 1.2s ease-in-out infinite' }}>{cfg.icon}</span>
                  : (isErr ? '✗' : '✓')}
              </span>
              <span style={{ color: cfg.color, fontWeight: 600, minWidth: 90 }}>{cfg.label}</span>
              <span style={{ color: 'var(--text-3)', fontSize: 10 }}>
                {isRun ? '…' : `${r.durationMs}ms`}
              </span>
              {r.summary && (
                <span style={{ color: 'var(--text-3)', fontSize: 10, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                  · {r.summary}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function LLMSelector({
  providers,
  brainProvider, setBrainProvider, brainModel, setBrainModel,
  qaProvider,    setQaProvider,    qaModel,    setQaModel,
  onReset,
}) {
  const { t } = useTranslation(['agent']);
  const providerOptions = [{ value: '', label: t('chat.useDefault') }, ...providers.map(p => ({ value: p.id, label: p.name }))];
  const modelsFor = (pid) => {
    if (!pid) return [{ value: '', label: t('chat.useDefault') }];
    const p = providers.find(x => x.id === pid);
    const ms = (p?.models || []).map(m => ({ value: m.id, label: m.name || m.id }));
    return [{ value: '', label: t('chat.useDefault') }, ...ms];
  };
  return (
    <div style={{ marginTop: 10, padding: 12, background: 'var(--bg-3)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div>
          <div style={{ fontSize: 10, color: 'var(--accent2)', fontWeight: 700, letterSpacing: '1px', marginBottom: 6 }}>
            🧠 {t('chat.brainLabel')}
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <SmallSelect
              value={brainProvider}
              onChange={v => { setBrainProvider(v); setBrainModel(''); }}
              options={providerOptions}
              label={t('chat.provider')}
            />
            <SmallSelect
              value={brainModel}
              onChange={setBrainModel}
              options={modelsFor(brainProvider)}
              label={t('chat.model')}
              disabled={!brainProvider}
            />
          </div>
        </div>
        <div>
          <div style={{ fontSize: 10, color: 'var(--green)', fontWeight: 700, letterSpacing: '1px', marginBottom: 6 }}>
            ✍️ {t('chat.synthLabel')}
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <SmallSelect
              value={qaProvider}
              onChange={v => { setQaProvider(v); setQaModel(''); }}
              options={providerOptions}
              label={t('chat.provider')}
            />
            <SmallSelect
              value={qaModel}
              onChange={setQaModel}
              options={modelsFor(qaProvider)}
              label={t('chat.model')}
              disabled={!qaProvider}
            />
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, fontSize: 10, color: 'var(--text-3)' }}>
        <span style={{ fontStyle: 'italic' }}>{t('chat.modelsHint')}</span>
        <button
          onClick={onReset}
          style={{
            display: 'flex', alignItems: 'center', gap: 4,
            padding: '4px 10px', borderRadius: 4,
            background: 'var(--bg-2)', border: '1px solid var(--border)',
            color: 'var(--text-2)', cursor: 'pointer', fontSize: 10, fontFamily: 'var(--font-mono)',
          }}
        >
          <RotateCcw size={10} /> {t('chat.reset')}
        </button>
      </div>
    </div>
  );
}

function SmallSelect({ value, onChange, options, label, disabled }) {
  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 9, color: 'var(--text-3)', letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: 3 }}>{label}</div>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        disabled={disabled}
        style={{
          width: '100%', padding: '5px 8px', borderRadius: 4,
          background: 'var(--bg-2)', border: '1px solid var(--border)',
          color: 'var(--text-1)', fontFamily: 'var(--font-mono)', fontSize: 11,
          cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.4 : 1,
        }}
      >
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

// Replace {{video:URL}} placeholders with React <video> elements interleaved
// with text. Renders text segments through ReactMarkdown.
function renderWithVideos(text) {
  if (!text) return null;
  const re = /\{\{\s*video\s*:\s*([^\s}]+)\s*\}\}/g;
  const out = [];
  let last = 0;
  let m;
  let idx = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) {
      out.push(<ReactMarkdown key={`t${idx++}`}>{text.slice(last, m.index)}</ReactMarkdown>);
    }
    out.push(<VideoEmbed key={`v${idx++}`} url={m[1]} />);
    last = m.index + m[0].length;
  }
  if (last < text.length) {
    out.push(<ReactMarkdown key={`t${idx++}`}>{text.slice(last)}</ReactMarkdown>);
  }
  return out;
}

function VideoEmbed({ url }) {
  return (
    <div style={{
      margin: '10px 0', borderRadius: 'var(--radius-sm)', overflow: 'hidden',
      border: '1px solid var(--border-bright)', maxWidth: 'min(520px, 100%)',
      boxShadow: '0 8px 24px rgba(0,0,0,0.45)',
    }}>
      <video
        controls
        src={url}
        style={{ width: '100%', display: 'block', background: 'black', maxWidth: '100%' }}
      />
      <div style={{
        padding: '4px 8px', background: 'var(--bg-2)', fontSize: 10,
        color: 'var(--text-3)', fontFamily: 'var(--font-mono)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <span>🎥 video</span>
        <a href={url} target="_blank" rel="noreferrer"
           style={{ color: 'var(--accent2)', textDecoration: 'none' }}>↗ apri</a>
      </div>
    </div>
  );
}

function ChatMessage({ msg, expanded, onToggle }) {
  const { t } = useTranslation(['agent']);
  const isUser = msg.role === 'user';
  return (
    <div className="animate-fade-up" style={{ display: 'flex', gap: 12, flexDirection: isUser ? 'row-reverse' : 'row' }}>
      <div style={{
        width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: isUser
          ? 'linear-gradient(135deg, var(--accent3) 0%, var(--accent) 100%)'
          : 'linear-gradient(135deg, var(--accent) 0%, var(--accent2) 100%)',
        color: '#fff',
        boxShadow: isUser
          ? '0 4px 12px var(--accent3-glow), inset 0 1px 0 rgba(255,255,255,0.2)'
          : '0 4px 12px var(--accent-glow-strong), inset 0 1px 0 rgba(255,255,255,0.2)',
      }}>
        {isUser ? <User size={16} /> : <Bot size={16} />}
      </div>
      <div style={{ flex: 1, maxWidth: '85%', minWidth: 0 }}>
        <div style={{
          padding: '12px 16px', borderRadius: 14,
          background: isUser
            ? 'linear-gradient(135deg, rgba(244,114,182,0.10) 0%, rgba(139,92,246,0.08) 100%)'
            : 'var(--bg-3)',
          border: `1px solid ${isUser ? 'rgba(244,114,182,0.25)' : 'var(--border)'}`,
          fontSize: 13, lineHeight: 1.65,
          boxShadow: 'var(--shadow-sm)',
          borderTopLeftRadius:  isUser ? 14 : 4,
          borderTopRightRadius: isUser ? 4  : 14,
          minWidth: 0, overflow: 'hidden',  // contain inner overflow
        }}>
          {isUser
            ? <div style={{ color: 'var(--text-1)', whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{msg.content}</div>
            : <div className="markdown-body">{renderWithVideos(msg.content)}</div>
          }
        </div>

        {!isUser && (msg.routing || msg.iterations !== undefined) && (
          <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap', fontFamily: 'var(--font-mono)', fontSize: 10 }}>
            {msg.persona && (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '2px 8px', borderRadius: 10, fontWeight: 700,
                background: `${msg.persona.color}25`, color: msg.persona.color,
                border: `1px solid ${msg.persona.color}50`,
              }}>{msg.persona.icon} {msg.persona.name}</span>
            )}
            {msg.fromCache && <Badge color="yellow">⚡ {t('common.cache')}</Badge>}
            {msg.routing?.selected && (
              <Badge color="cyan">{msg.routing.selected.length} {t('chat.routing')}</Badge>
            )}
            {msg.iterations !== undefined && (
              <Badge color="cyan">iter: {msg.iterations}</Badge>
            )}
            {msg.latencyMs && (
              <Badge color="green">{msg.latencyMs}ms</Badge>
            )}
            {msg.cost?.usd > 0 && (
              <Badge color="purple">${msg.cost.usd.toFixed(4)} · {msg.cost.tokens}tok</Badge>
            )}
            {msg.citationsVerified && (
              <Badge color={msg.citationsVerified.score >= 0.9 ? 'green' : msg.citationsVerified.score >= 0.5 ? 'yellow' : 'red'}>
                {msg.citationsVerified.score >= 0.9 ? '✓' : '⚠'} {t('common.verified')} {(msg.citationsVerified.score * 100).toFixed(0)}%
              </Badge>
            )}
            {msg.fallbackUsed && (
              <Badge color="red">{t('chat.fallback')}</Badge>
            )}
            {msg.toolCalls?.length > 0 && (
              <Badge color="yellow">{msg.toolCalls.length} {t('chat.tools')}</Badge>
            )}
            {msg.citations?.length > 0 && (
              <Badge color="purple">{msg.citations.length} {t('chat.sources')}</Badge>
            )}
            <SpeakButton text={msg.content} />
            {(msg.trace || msg.routing) && (
              <button
                onClick={onToggle}
                style={{
                  fontSize: 10, fontFamily: 'var(--font-mono)', padding: '2px 8px',
                  background: 'var(--bg-3)', border: '1px solid var(--border)',
                  borderRadius: 4, color: 'var(--text-3)', cursor: 'pointer',
                }}
              >
                {expanded ? t('chat.hideTrace') : t('chat.showTrace')}
              </button>
            )}
          </div>
        )}

        {!isUser && expanded && (
          <div style={{ marginTop: 8 }}>
            {msg.routing && <RoutingPanel routing={msg.routing} />}
            {msg.trace && <MiniTraceTimeline trace={msg.trace} />}
            {msg.citations?.length > 0 && <CitationsList citations={msg.citations} />}
            {msg.toolCalls?.length > 0 && <ToolCallsPanel toolCalls={msg.toolCalls} />}
          </div>
        )}
      </div>
    </div>
  );
}

function RoutingPanel({ routing }) {
  const { t } = useTranslation(['agent']);
  if (!routing?.candidates) return null;
  return (
    <div style={{ marginTop: 8, padding: 10, background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)' }}>
      <div style={{ fontSize: 10, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 6, fontWeight: 700 }}>
        {t('chat.routing')}
      </div>
      {routing.candidates.map((c, i) => (
        <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 4, fontSize: 11, fontFamily: 'var(--font-mono)' }}>
          <Badge color="cyan">{(c.score * 100).toFixed(0)}%</Badge>
          <span style={{ color: 'var(--text-1)' }}>{c.name}</span>
          <span style={{ color: 'var(--text-3)' }}>— {c.reason}</span>
        </div>
      ))}
    </div>
  );
}

function MiniTraceTimeline({ trace }) {
  const { t } = useTranslation(['agent']);
  const total = trace.reduce((s, x) => s + (x.durationMs || 0), 0) || 1;
  return (
    <div style={{ marginTop: 8, padding: 10, background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)' }}>
      <div style={{ fontSize: 10, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 8, fontWeight: 700 }}>
        {t('chat.trace')} ({total}ms)
      </div>
      <div style={{ display: 'flex', gap: 2, height: 18, marginBottom: 8 }}>
        {trace.map((s, i) => {
          const c = NODE_COLORS[s.node] || NODE_COLORS.router;
          const pct = Math.max(2, ((s.durationMs || 0) / total) * 100);
          return (
            <div key={i} title={`${s.node}: ${s.durationMs}ms — ${s.summary || ''}`}
              style={{ width: `${pct}%`, background: c.bg, border: `1px solid ${c.bd}`, borderRadius: 2 }} />
          );
        })}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontFamily: 'var(--font-mono)', fontSize: 11 }}>
        {trace.map((s, i) => {
          const c = NODE_COLORS[s.node] || NODE_COLORS.router;
          return (
            <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ minWidth: 110, color: c.fg, fontWeight: 600 }}>{s.node}</span>
              <span style={{ minWidth: 60, color: 'var(--text-3)' }}>{s.durationMs}ms</span>
              <span style={{ color: 'var(--text-2)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {s.summary}
              </span>
              {s.ok ? <CheckCircle2 size={11} style={{ color: 'var(--green)' }} />
                    : <AlertTriangle size={11} style={{ color: 'var(--red)' }} />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CitationsList({ citations }) {
  const { t } = useTranslation(['agent']);
  return (
    <div style={{ marginTop: 8, padding: 10, background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)' }}>
      <div style={{ fontSize: 10, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 6, fontWeight: 700 }}>
        {t('chat.sources')} ({citations.length})
      </div>
      {citations.map((c, i) => (
        <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 6, fontSize: 11, fontFamily: 'var(--font-mono)' }}>
          <span style={{
            width: 18, height: 18, borderRadius: 4, background: 'var(--accent-glow)',
            border: '1px solid var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'var(--accent)', fontWeight: 700, fontSize: 10, flexShrink: 0,
          }}>{i + 1}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              {c.instanceName && <Badge color="purple">{c.instanceName}</Badge>}
              <span style={{ color: 'var(--text-1)' }}>{c.source}</span>
              {c.pageNum && <Badge color="cyan">p.{c.pageNum}</Badge>}
              {c.score && <span style={{ color: 'var(--green)' }}>{(c.score * 100).toFixed(0)}%</span>}
            </div>
            {(c.text || c.snippet) && (
              <div style={{ color: 'var(--text-3)', fontSize: 10, marginTop: 2, fontStyle: 'italic' }}>
                {(c.text || c.snippet || '').substring(0, 180)}
              </div>
            )}
            {c.videoUrl && (
              <video
                controls
                src={c.timestampSec ? `${c.videoUrl}#t=${c.timestampSec.toFixed(1)}` : c.videoUrl}
                style={{
                  marginTop: 6, maxWidth: 320, width: '100%',
                  borderRadius: 6, border: '1px solid var(--border)',
                  background: 'black', display: 'block',
                }}
              />
            )}
            {c.imageUrl && !c.videoUrl && (
              <img src={c.imageUrl} alt={c.source}
                style={{ marginTop: 6, maxWidth: 180, maxHeight: 120, borderRadius: 4, border: '1px solid var(--border)', objectFit: 'cover', display: 'block' }} />
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function ToolCallsPanel({ toolCalls }) {
  return (
    <div style={{ marginTop: 8, padding: 10, background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)' }}>
      <div style={{ fontSize: 10, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 6, fontWeight: 700 }}>
        Tool calls ({toolCalls.length})
      </div>
      {toolCalls.map((tc, i) => (
        <div key={i} style={{ marginBottom: 6, fontSize: 11, fontFamily: 'var(--font-mono)' }}>
          <div style={{ color: 'var(--accent2)', fontWeight: 600 }}>{tc.name}({JSON.stringify(tc.args).substring(0, 80)})</div>
          <div style={{ color: 'var(--text-3)', fontSize: 10 }}>
            → {JSON.stringify(tc.result).substring(0, 160)} ({tc.durationMs}ms)
          </div>
        </div>
      ))}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  TAB: GRAPH
// ════════════════════════════════════════════════════════════════════════════
function GraphTab({ lastRun }) {
  const { t } = useTranslation(['agent']);
  const trace = lastRun?.trace || [];

  return (
    <Card style={{ padding: 24 }}>
      <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 16, marginBottom: 4 }}>
        {t('graph.title')}
      </h3>
      <p style={{ color: 'var(--text-3)', fontSize: 12, marginBottom: 24 }}>{t('graph.subtitle')}</p>

      <GraphSVG trace={trace} />

      {trace.length > 0 ? (
        <>
          <div style={{ display: 'flex', gap: 16, marginTop: 24, marginBottom: 16, flexWrap: 'wrap' }}>
            <StatPill label={t('graph.totalMs')} value={`${lastRun.latencyMs || trace.reduce((s, x) => s + (x.durationMs || 0), 0)}ms`} color="var(--accent)" />
            <StatPill label={t('graph.stepsCount')} value={trace.length} color="var(--accent2)" />
            <StatPill label={t('chat.iterations')} value={lastRun.iterations || 0} color="var(--yellow)" />
            <StatPill label={t('chat.sources')} value={(lastRun.sources || []).length} color="var(--green)" />
          </div>
          <TimelineFull trace={trace} />
        </>
      ) : (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-3)', fontStyle: 'italic' }}>
          {t('graph.noRun')}
        </div>
      )}

      <NodeLegend />
    </Card>
  );
}

function StatPill({ label, value, color }) {
  return (
    <div style={{
      padding: '6px 14px', borderRadius: 999, background: 'var(--bg-2)',
      border: `1px solid ${color}`, fontFamily: 'var(--font-mono)', fontSize: 12,
    }}>
      <span style={{ color: 'var(--text-3)' }}>{label}: </span>
      <span style={{ color, fontWeight: 700 }}>{value}</span>
    </div>
  );
}

function GraphSVG({ trace }) {
  const visited = new Set(trace.map(s => s.node));
  const counts = trace.reduce((acc, s) => { acc[s.node] = (acc[s.node] || 0) + 1; return acc; }, {});

  // Compute layout positions
  const nodes = [
    { id: 'router',       label: 'Router',        x: 80,   y: 130 },
    { id: 'retriever',    label: 'Retriever',     x: 260,  y: 130 },
    { id: 'critic',       label: 'Critic',        x: 440,  y: 130 },
    { id: 'synthesizer',  label: 'Synthesizer',   x: 620,  y: 130 },
    { id: 'memoryWriter', label: 'Memory Writer', x: 820,  y: 130 },
  ];

  const edges = [
    { from: 'router',      to: 'retriever',     label: 'has instances' },
    { from: 'retriever',   to: 'critic',        label: '' },
    { from: 'critic',      to: 'synthesizer',   label: 'sufficient' },
    { from: 'synthesizer', to: 'memoryWriter',  label: '' },
  ];

  return (
    <div style={{ background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: 16, overflowX: 'auto' }}>
      <svg viewBox="0 0 920 220" width="100%" height="240" style={{ minWidth: 880 }}>
        {/* Edges */}
        {edges.map((e, i) => {
          const f = nodes.find(n => n.id === e.from);
          const t = nodes.find(n => n.id === e.to);
          const active = visited.has(e.from) && visited.has(e.to);
          return (
            <g key={i}>
              <line
                x1={f.x + 60} y1={f.y}
                x2={t.x - 60} y2={t.y}
                stroke={active ? 'var(--accent)' : 'var(--border)'}
                strokeWidth={active ? 2.5 : 1.5}
                markerEnd={active ? 'url(#arrowActive)' : 'url(#arrow)'}
              />
              {e.label && (
                <text x={(f.x + t.x) / 2} y={f.y - 8} textAnchor="middle"
                  fill={active ? 'var(--accent)' : 'var(--text-3)'}
                  style={{ fontSize: 9, fontFamily: 'var(--font-mono)' }}>
                  {e.label}
                </text>
              )}
            </g>
          );
        })}

        {/* Critic → Retriever loop edge (refine) */}
        {(() => {
          const ret = nodes.find(n => n.id === 'retriever');
          const cri = nodes.find(n => n.id === 'critic');
          const active = (counts.retriever || 0) > 1;
          return (
            <g>
              <path
                d={`M ${cri.x - 30} ${cri.y + 30} Q ${(ret.x + cri.x) / 2} ${cri.y + 70}, ${ret.x + 30} ${ret.y + 30}`}
                fill="none"
                stroke={active ? '#f59e0b' : 'var(--border)'}
                strokeWidth={active ? 2.5 : 1.5}
                strokeDasharray={active ? '0' : '4 3'}
                markerEnd={active ? 'url(#arrowLoop)' : 'url(#arrow)'}
              />
              <text x={(ret.x + cri.x) / 2} y={cri.y + 82} textAnchor="middle"
                fill={active ? '#f59e0b' : 'var(--text-3)'}
                style={{ fontSize: 9, fontFamily: 'var(--font-mono)', fontStyle: 'italic' }}>
                refine query (loop)
              </text>
            </g>
          );
        })()}

        {/* Nodes */}
        {nodes.map(n => {
          const c = NODE_COLORS[n.id];
          const v = visited.has(n.id);
          const cnt = counts[n.id] || 0;
          return (
            <g key={n.id}>
              <rect
                x={n.x - 60} y={n.y - 25}
                width={120} height={50}
                rx={8}
                fill={v ? c.bg : 'var(--bg-3)'}
                stroke={v ? c.bd : 'var(--border)'}
                strokeWidth={v ? 2 : 1}
              />
              <text x={n.x} y={n.y + 5} textAnchor="middle"
                fill={v ? c.fg : 'var(--text-3)'}
                style={{ fontSize: 12, fontFamily: 'var(--font-mono)', fontWeight: 700 }}>
                {n.label}
              </text>
              {cnt > 1 && (
                <g>
                  <circle cx={n.x + 55} cy={n.y - 20} r={11} fill="#f59e0b" />
                  <text x={n.x + 55} y={n.y - 17} textAnchor="middle" fill="#fff" style={{ fontSize: 10, fontWeight: 700 }}>×{cnt}</text>
                </g>
              )}
            </g>
          );
        })}

        {/* Arrow defs */}
        <defs>
          <marker id="arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto">
            <path d="M0,0 L10,5 L0,10 z" fill="var(--border)" />
          </marker>
          <marker id="arrowActive" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto">
            <path d="M0,0 L10,5 L0,10 z" fill="var(--accent)" />
          </marker>
          <marker id="arrowLoop" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto">
            <path d="M0,0 L10,5 L0,10 z" fill="#f59e0b" />
          </marker>
        </defs>
      </svg>
    </div>
  );
}

function NodeLegend() {
  const { t } = useTranslation(['agent']);
  const items = ['router', 'retriever', 'critic', 'synthesizer', 'memoryWriter'];
  return (
    <div style={{ marginTop: 20, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
      {items.map(id => {
        const c = NODE_COLORS[id];
        return (
          <div key={id} style={{
            padding: 12, background: c.bg, border: `1px solid ${c.bd}`, borderRadius: 'var(--radius-sm)',
          }}>
            <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, color: c.fg, fontSize: 12, marginBottom: 4 }}>
              {t(`graph.nodes.${id}`)}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-2)', lineHeight: 1.5 }}>
              {t(`graph.nodeDescriptions.${id}`)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TimelineFull({ trace }) {
  const { t } = useTranslation(['agent']);
  const total = trace.reduce((s, x) => s + (x.durationMs || 0), 0) || 1;
  return (
    <div style={{ background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: 14 }}>
      <div style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 12, fontWeight: 700 }}>
        {t('graph.timeline')}
      </div>
      <div style={{ display: 'flex', gap: 2, height: 26, marginBottom: 12 }}>
        {trace.map((s, i) => {
          const c = NODE_COLORS[s.node] || NODE_COLORS.router;
          const pct = Math.max(2, ((s.durationMs || 0) / total) * 100);
          return (
            <div key={i} title={`${s.node}: ${s.durationMs}ms`}
              style={{
                width: `${pct}%`, background: c.bg, border: `1px solid ${c.bd}`,
                borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: 'var(--font-mono)', fontSize: 9, color: c.fg, fontWeight: 600, overflow: 'hidden',
              }}>
              {pct > 8 ? s.node : ''}
            </div>
          );
        })}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {trace.map((s, i) => {
          const c = NODE_COLORS[s.node] || NODE_COLORS.router;
          return (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px', background: 'var(--bg-3)', borderRadius: 4, fontFamily: 'var(--font-mono)', fontSize: 11 }}>
              <span style={{ minWidth: 18, textAlign: 'right', color: 'var(--text-3)' }}>{i + 1}.</span>
              <span style={{ minWidth: 120, color: c.fg, fontWeight: 600 }}>{s.node}</span>
              <span style={{ minWidth: 70, color: 'var(--text-3)' }}>{s.durationMs}ms</span>
              <span style={{ color: 'var(--text-2)', flex: 1 }}>{s.summary}</span>
              {s.ok ? <CheckCircle2 size={12} style={{ color: 'var(--green)' }} /> : <AlertTriangle size={12} style={{ color: 'var(--red)' }} />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  TAB: SESSIONS
// ════════════════════════════════════════════════════════════════════════════
function SessionsTab() {
  const { t } = useTranslation(['agent', 'common']);
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['agent-sessions'], queryFn: agentApi.listSessions });

  const del = useMutation({
    mutationFn: id => agentApi.deleteSession(id),
    onSuccess: () => { qc.invalidateQueries(['agent-sessions']); toast.success('Sessione eliminata'); },
  });

  if (isLoading) return <Spinner />;
  const sessions = data?.sessions || [];

  if (sessions.length === 0) {
    return <EmptyState icon={Layers} title={t('tabs.sessions')} description={t('sessions.empty')} />;
  }

  return (
    <Card style={{ padding: 0, overflow: 'hidden' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, fontFamily: 'var(--font-mono)' }}>
        <thead style={{ background: 'var(--bg-2)' }}>
          <tr>
            <th style={th}>{t('sessions.title')}</th>
            <th style={th}>ID</th>
            <th style={th}>{t('sessions.lastActive')}</th>
            <th style={th}>{t('sessions.created')}</th>
            <th style={th}></th>
          </tr>
        </thead>
        <tbody>
          {sessions.map(s => (
            <tr key={s.id} style={{ borderTop: '1px solid var(--border)' }}>
              <td style={td}>{s.title || '—'}</td>
              <td style={{ ...td, color: 'var(--text-3)' }}>{s.id.substring(0, 8)}…</td>
              <td style={td}>{new Date(s.lastActiveAt).toLocaleString()}</td>
              <td style={td}>{new Date(s.createdAt).toLocaleString()}</td>
              <td style={{ ...td, textAlign: 'right' }}>
                <button
                  onClick={() => { if (window.confirm(t('sessions.confirmDelete'))) del.mutate(s.id); }}
                  style={iconBtn('var(--red)')}
                ><Trash2 size={13} /></button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  TAB: MEMORY (facts)
// ════════════════════════════════════════════════════════════════════════════
function MemoryTab() {
  const { t } = useTranslation(['agent']);
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['agent-facts'], queryFn: agentApi.listFacts });
  const del = useMutation({
    mutationFn: id => agentApi.deleteFact(id),
    onSuccess: () => { qc.invalidateQueries(['agent-facts']); toast.success('Fatto eliminato'); },
  });

  if (isLoading) return <Spinner />;
  const facts = data?.facts || [];

  return (
    <>
      <p style={{ color: 'var(--text-3)', fontSize: 13, marginBottom: 16 }}>{t('memory.subtitle')}</p>
      {facts.length === 0 ? (
        <EmptyState icon={Brain} title={t('tabs.memory')} description={t('memory.empty')} />
      ) : (
        <Card style={{ padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, fontFamily: 'var(--font-mono)' }}>
            <thead style={{ background: 'var(--bg-2)' }}>
              <tr>
                <th style={th}>{t('memory.key')}</th>
                <th style={th}>{t('memory.value')}</th>
                <th style={th}>{t('memory.confidence')}</th>
                <th style={th}>{t('memory.updated')}</th>
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {facts.map(f => (
                <tr key={f.id} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ ...td, color: 'var(--accent)', fontWeight: 600 }}>{f.key}</td>
                  <td style={td}>{f.value}</td>
                  <td style={td}>
                    <Badge color={f.confidence > 0.85 ? 'green' : 'yellow'}>
                      {(f.confidence * 100).toFixed(0)}%
                    </Badge>
                  </td>
                  <td style={td}>{new Date(f.updatedAt).toLocaleString()}</td>
                  <td style={{ ...td, textAlign: 'right' }}>
                    <button onClick={() => del.mutate(f.id)} style={iconBtn('var(--red)')}><Trash2 size={13} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  TAB: TOOLS
// ════════════════════════════════════════════════════════════════════════════
function ToolsTab() {
  const { t } = useTranslation(['agent']);
  const { data, isLoading } = useQuery({ queryKey: ['agent-tools'], queryFn: agentApi.tools });

  if (isLoading) return <Spinner />;
  const tools = data?.tools || [];

  return (
    <>
      <p style={{ color: 'var(--text-3)', fontSize: 13, marginBottom: 16 }}>{t('tools.subtitle')}</p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 12 }}>
        {tools.map(tool => (
          <Card key={tool.name} style={{ padding: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <div style={{
                width: 28, height: 28, borderRadius: 'var(--radius-sm)',
                background: 'rgba(6,182,212,0.18)', border: '1px solid var(--accent2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent2)',
              }}>
                <Wrench size={14} />
              </div>
              <code style={{ color: 'var(--accent2)', fontSize: 13, fontWeight: 700 }}>{tool.name}</code>
            </div>
            <p style={{ fontSize: 12, color: 'var(--text-2)', lineHeight: 1.5, marginBottom: 12 }}>
              {tool.description}
            </p>
            <div style={{ fontSize: 10, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 4 }}>
              {t('tools.schema')}
            </div>
            <pre style={{
              fontSize: 11, fontFamily: 'var(--font-mono)', background: 'var(--bg-3)',
              padding: 8, borderRadius: 4, border: '1px solid var(--border)',
              color: 'var(--text-2)', overflowX: 'auto', margin: 0,
            }}>{JSON.stringify(tool.schema, null, 2)}</pre>
          </Card>
        ))}
      </div>
    </>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  TAB: TRACES
// ════════════════════════════════════════════════════════════════════════════
function TracesTab() {
  const { t } = useTranslation(['agent']);
  const { data, isLoading } = useQuery({ queryKey: ['agent-traces'], queryFn: () => agentApi.listTraces({ limit: 100 }), refetchInterval: 5000 });
  const [openId, setOpenId] = useState(null);

  if (isLoading) return <Spinner />;
  const traces = data?.traces || [];

  return (
    <>
      <p style={{ color: 'var(--text-3)', fontSize: 13, marginBottom: 16 }}>{t('traces.subtitle')}</p>
      {traces.length === 0 ? (
        <EmptyState icon={Activity} title={t('tabs.traces')} description={t('traces.empty')} />
      ) : (
        <Card style={{ padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, fontFamily: 'var(--font-mono)' }}>
            <thead style={{ background: 'var(--bg-2)' }}>
              <tr>
                <th style={th}>{t('traces.query')}</th>
                <th style={th}>{t('traces.selected')}</th>
                <th style={th}>{t('traces.iterations')}</th>
                <th style={th}>{t('traces.totalMs')}</th>
                <th style={th}>{t('traces.status')}</th>
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {traces.map(tr => (
                <tr key={tr.id} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ ...td, maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {tr.query}
                  </td>
                  <td style={td}>
                    {tr.routing?.candidates?.map(c => (
                      <Badge key={c.id} color="cyan">{c.name}</Badge>
                    )) || '—'}
                  </td>
                  <td style={td}>{tr.iterations ?? '—'}</td>
                  <td style={td}>{tr.totalMs ?? '—'}ms</td>
                  <td style={td}>
                    <Badge color={tr.status === 'ok' ? 'green' : tr.status === 'fallback' ? 'yellow' : 'red'}>
                      {tr.status}
                    </Badge>
                  </td>
                  <td style={{ ...td, textAlign: 'right' }}>
                    <button onClick={() => setOpenId(tr.id)} style={iconBtn('var(--accent2)')}><Eye size={13} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
      {openId && <TraceDetailModal id={openId} onClose={() => setOpenId(null)} />}
    </>
  );
}

function TraceDetailModal({ id, onClose }) {
  const { t } = useTranslation(['agent']);
  const { data, isLoading } = useQuery({ queryKey: ['agent-trace', id], queryFn: () => agentApi.getTrace(id) });

  return (
    <ModalOverlay onClose={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'var(--bg-2)', border: '1px solid var(--border-bright)', borderRadius: 'var(--radius)',
        padding: 24, width: 'min(900px, 100%)', maxHeight: 'calc(100dvh - 40px)', overflowY: 'auto',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 16 }}>Trace detail</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-3)', cursor: 'pointer' }}><X size={18} /></button>
        </div>
        {isLoading ? <Spinner /> : data && (
          <>
            <div style={{ marginBottom: 16, padding: 12, background: 'var(--bg-3)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)' }}>
              <div style={{ fontSize: 10, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 4 }}>Query</div>
              <div style={{ fontSize: 13, color: 'var(--text-1)' }}>{data.query}</div>
            </div>
            <GraphSVG trace={data.trace} />
            <div style={{ marginTop: 16 }}>
              <TimelineFull trace={data.trace || []} />
            </div>
            {data.error && (
              <div style={{ marginTop: 16, padding: 12, background: 'rgba(239,68,68,0.1)', border: '1px solid var(--red)', borderRadius: 'var(--radius-sm)', color: 'var(--red)', fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                <AlertTriangle size={14} style={{ display: 'inline', marginRight: 6 }} />
                {data.error}
              </div>
            )}
          </>
        )}
      </div>
    </ModalOverlay>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  TAB: CONFIG
// ════════════════════════════════════════════════════════════════════════════
function ConfigTab() {
  const { t } = useTranslation(['agent']);
  const qc = useQueryClient();
  const currentUser = useStore(s => s.currentUser);
  const isSuperAdmin = currentUser?.role === 'super_admin';
  const { data, isLoading } = useQuery({ queryKey: ['agent-config'], queryFn: agentApi.config });
  const [draft, setDraft] = useState(null);

  // Initialize draft from server settings on first load / refetch
  useEffect(() => {
    if (data?.settings) setDraft({ ...data.settings });
  }, [data?.settings]);

  const saveMut = useMutation({
    mutationFn: (diff) => agentApi.updateConfig(diff),
    onSuccess: () => { toast.success('Configurazione salvata'); qc.invalidateQueries(['agent-config']); },
    onError:   (err) => toast.error(err.message || 'Salvataggio fallito'),
  });

  if (isLoading || !draft) return <Spinner />;
  const schema = data?.schema || {};
  const settings = data?.settings || {};

  // Diff: only changed keys
  const diff = {};
  for (const k of Object.keys(draft)) {
    if (String(draft[k]) !== String(settings[k])) diff[k] = draft[k];
  }
  const hasChanges = Object.keys(diff).length > 0;

  const set = (k, v) => setDraft(d => ({ ...d, [k]: v }));
  const reset = () => setDraft({ ...settings });

  return (
    <div style={{ maxWidth: 760 }}>
      {!isSuperAdmin && (
        <div style={{
          marginBottom: 14, padding: 10, background: 'var(--yellow-glow)',
          border: '1px solid var(--yellow)', borderRadius: 'var(--radius-sm)',
          fontSize: 12, color: 'var(--yellow)',
        }}>
          ⚠️ Solo super_admin può modificare. I controlli sono visibili in modalità read-only.
        </div>
      )}

      <Card style={{ padding: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }}>
          <div>
            <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 16, marginBottom: 4 }}>
              {t('config.title')}
            </h3>
            <p style={{ fontSize: 11, color: 'var(--text-3)' }}>
              Modifiche persistite in MySQL e applicate live (no restart). Fallback su env vars se non override.
            </p>
          </div>
          {hasChanges && (
            <Badge color="yellow">{Object.keys(diff).length} non salvate</Badge>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {Object.entries(schema).map(([key, spec]) => (
            <ConfigRow
              key={key}
              k={key}
              spec={spec}
              value={draft[key]}
              changed={diff[key] !== undefined}
              disabled={!isSuperAdmin}
              onChange={v => set(key, v)}
            />
          ))}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
          <Button variant="ghost" onClick={reset} disabled={!hasChanges}>Reset</Button>
          <Button
            onClick={() => saveMut.mutate(diff)}
            disabled={!hasChanges || !isSuperAdmin}
            loading={saveMut.isPending}
            icon={CheckCircle2}
          >
            Salva modifiche
          </Button>
        </div>
      </Card>
    </div>
  );
}

function ConfigRow({ k, spec, value, changed, disabled, onChange }) {
  const renderInput = () => {
    if (spec.type === 'bool') {
      return (
        <button
          onClick={() => !disabled && onChange(!value)}
          disabled={disabled}
          style={{
            width: 44, height: 24, borderRadius: 12,
            background: value ? 'var(--green)' : 'var(--bg-4)',
            border: `1px solid ${value ? 'var(--green)' : 'var(--border)'}`,
            position: 'relative', cursor: disabled ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s', opacity: disabled ? 0.5 : 1,
          }}
        >
          <div style={{
            position: 'absolute', top: 2, left: value ? 22 : 2,
            width: 18, height: 18, borderRadius: '50%',
            background: '#fff', transition: 'left 0.2s',
            boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
          }} />
        </button>
      );
    }
    if (spec.type === 'enum') {
      return (
        <select
          value={String(value)}
          onChange={e => onChange(e.target.value)}
          disabled={disabled}
          style={{
            padding: '6px 10px', borderRadius: 4,
            background: 'var(--bg-3)', border: '1px solid var(--border)',
            color: 'var(--text-1)', fontFamily: 'var(--font-mono)', fontSize: 12,
            cursor: disabled ? 'not-allowed' : 'pointer', minWidth: 140,
          }}
        >
          {spec.enum.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      );
    }
    if (spec.type === 'int' || spec.type === 'float') {
      return (
        <input
          type="number"
          value={value}
          step={spec.type === 'float' ? '0.01' : '1'}
          min={spec.min} max={spec.max}
          onChange={e => onChange(spec.type === 'int' ? parseInt(e.target.value, 10) : parseFloat(e.target.value))}
          disabled={disabled}
          style={{
            padding: '6px 10px', borderRadius: 4,
            background: 'var(--bg-3)', border: '1px solid var(--border)',
            color: 'var(--text-1)', fontFamily: 'var(--font-mono)', fontSize: 12,
            width: 100, textAlign: 'right',
          }}
        />
      );
    }
    // string
    return (
      <input
        type="text"
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        disabled={disabled}
        style={{
          padding: '6px 10px', borderRadius: 4,
          background: 'var(--bg-3)', border: '1px solid var(--border)',
          color: 'var(--text-1)', fontFamily: 'var(--font-mono)', fontSize: 12,
          minWidth: 200,
        }}
      />
    );
  };

  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '1fr auto', gap: 16,
      alignItems: 'center',
      padding: '12px 14px',
      background: changed ? 'var(--accent-glow)' : 'var(--bg-3)',
      border: `1px solid ${changed ? 'var(--accent)' : 'var(--border)'}`,
      borderRadius: 'var(--radius-sm)',
      transition: 'all 0.2s',
    }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <code style={{ fontSize: 11, color: changed ? 'var(--accent)' : 'var(--text-1)', fontWeight: 700 }}>
            {k}
          </code>
          {spec.min != null && spec.max != null && (
            <span style={{ fontSize: 9, color: 'var(--text-3)', fontFamily: 'var(--font-mono)' }}>
              [{spec.min}…{spec.max}]
            </span>
          )}
          {changed && <Badge color="cyan">modificato</Badge>}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 3 }}>
          {spec.description}
        </div>
      </div>
      {renderInput()}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  Speak (browser TTS)
// ════════════════════════════════════════════════════════════════════════════
function SpeakButton({ text }) {
  const [speaking, setSpeaking] = useState(false);
  const speak = () => {
    if (!('speechSynthesis' in window) || !text) return;
    if (speaking) {
      window.speechSynthesis.cancel();
      setSpeaking(false);
      return;
    }
    // Strip markdown markers & images for cleaner audio
    const clean = String(text)
      .replace(/!?\[[^\]]*\]\([^)]*\)/g, ' ')
      .replace(/[*_`#>]+/g, '')
      .substring(0, 4000);
    const u = new SpeechSynthesisUtterance(clean);
    u.lang = (navigator.language || 'it-IT').startsWith('it') ? 'it-IT' : 'en-US';
    u.onend = () => setSpeaking(false);
    u.onerror = () => setSpeaking(false);
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(u);
    setSpeaking(true);
  };
  return (
    <button
      onClick={speak}
      title={speaking ? 'Stop' : 'Read'}
      style={{
        fontSize: 10, fontFamily: 'var(--font-mono)', padding: '2px 8px',
        background: speaking ? 'var(--accent-glow)' : 'var(--bg-3)',
        border: '1px solid var(--border)', borderRadius: 4,
        color: speaking ? 'var(--accent)' : 'var(--text-3)', cursor: 'pointer',
        display: 'inline-flex', alignItems: 'center', gap: 4,
      }}
    >
      {speaking ? <VolumeX size={11} /> : <Volume2 size={11} />}
    </button>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  TAB: COST
// ════════════════════════════════════════════════════════════════════════════
function CostTab() {
  const { t } = useTranslation(['agent']);
  const [days, setDays] = useState(30);
  const { data, isLoading } = useQuery({ queryKey: ['agent-cost', days], queryFn: () => agentApi.cost(days), refetchInterval: 10000 });

  if (isLoading) return <Spinner />;
  const d = data || {};
  const total = d.total || { usd: 0, tokens: 0, calls: 0 };
  const hasData = total.calls > 0;

  return (
    <>
      <p style={{ color: 'var(--text-3)', fontSize: 13, marginBottom: 16 }}>
        {t('cost.subtitle')}
      </p>

      <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
        {[1, 7, 30, 90, 365].map(n => (
          <button key={n} onClick={() => setDays(n)} style={{
            padding: '6px 12px', fontSize: 11, fontFamily: 'var(--font-mono)',
            background: days === n ? 'var(--accent-glow)' : 'var(--bg-3)',
            border: `1px solid ${days === n ? 'var(--accent)' : 'var(--border)'}`,
            color: days === n ? 'var(--accent)' : 'var(--text-2)',
            borderRadius: 'var(--radius-sm)', cursor: 'pointer',
          }}>{n === 365 ? '1y' : `${n}d`}</button>
        ))}
      </div>

      {!hasData ? (
        <EmptyState icon={DollarSign} title={t('tabs.cost')} description={t('cost.noData')} />
      ) : (
        <>
          <div className="grid-stats">
            <StatCard label={t('cost.totalUsd')}    value={`$${total.usd.toFixed(4)}`} icon={DollarSign} color="var(--green)" />
            <StatCard label={t('cost.totalTokens')} value={Number(total.tokens).toLocaleString()} icon={Hash} color="var(--accent2)" />
            <StatCard label={t('cost.calls')}       value={total.calls} icon={Zap} color="var(--accent)" />
            <StatCard label="Cache size"            value={<CacheSizeStat />} icon={ZapIcon} color="var(--yellow)" />
          </div>

          <div className="grid-2col">
            <Card style={{ padding: 18 }}>
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 700, marginBottom: 12 }}>
                {t('cost.byModel')}
              </h3>
              <CostBarChart rows={d.byModel || []} keyField="model" colorOf={i => `hsl(${i * 47 % 360}, 70%, 60%)`} />
            </Card>
            <Card style={{ padding: 18 }}>
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 700, marginBottom: 12 }}>
                {t('cost.byRole')}
              </h3>
              <CostBarChart rows={d.byRole || []} keyField="role" colorOf={i => `hsl(${i * 70 % 360}, 65%, 60%)`} />
            </Card>
          </div>

          <Card style={{ padding: 18, marginTop: 16 }}>
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 700, marginBottom: 12 }}>
              {t('cost.byInstance')}
            </h3>
            <CostBarChart
              rows={(d.byInstance || []).map(r => ({ ...r, label: r.instanceName }))}
              keyField="label"
              colorOf={i => `hsl(${(i * 53 + 200) % 360}, 65%, 60%)`}
            />
          </Card>

          <Card style={{ padding: 18, marginTop: 16 }}>
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 700, marginBottom: 4 }}>
              {t('cost.daily')}
            </h3>
            <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 12 }}>
              {t('cost.dailyHint', { days })}
            </div>
            <DailySpark daily={d.daily || []} days={days} />
          </Card>
        </>
      )}
    </>
  );
}

function CacheSizeStat() {
  const { data } = useQuery({ queryKey: ['agent-cache-stats'], queryFn: agentApi.cacheStats, refetchInterval: 15000 });
  return <span>{data?.points ?? 0}</span>;
}

function CostBarChart({ rows, keyField, colorOf }) {
  const max = Math.max(...rows.map(r => r.usd || 0), 0.000001);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {rows.map((r, i) => {
        const pct = (r.usd / max) * 100;
        const color = colorOf(i);
        return (
          <div key={i}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontFamily: 'var(--font-mono)', fontSize: 11 }}>
              <span style={{ color: 'var(--text-1)', fontWeight: 600 }}>{r[keyField]}</span>
              <span style={{ color: 'var(--text-3)' }}>${r.usd.toFixed(4)} · {Number(r.tokens).toLocaleString()}tok · {r.calls}x</span>
            </div>
            <div style={{ height: 6, background: 'var(--bg-3)', borderRadius: 3, overflow: 'hidden' }}>
              <div style={{ width: pct + '%', height: '100%', background: color, transition: 'width 0.4s ease' }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DailySpark({ daily, days = 30 }) {
  // Fill the time-series with missing days so the chart shows the actual
  // span (not just the few days where there was activity).
  const map = new Map((daily || []).map(d => [String(d.day).substring(0, 10), d]));
  const series = [];
  const end = new Date();
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(end);
    d.setDate(end.getDate() - i);
    const key = d.toISOString().substring(0, 10);
    const row = map.get(key);
    series.push({
      day:    key,
      label:  d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' }),
      usd:    row ? parseFloat(row.usd) : 0,
      tokens: row ? row.tokens : 0,
      calls:  row ? row.calls  : 0,
    });
  }
  if (!series.length || series.every(s => s.usd === 0)) {
    return <div style={{ color: 'var(--text-3)', fontSize: 12, padding: 16, textAlign: 'center' }}>Nessuna attività negli ultimi {days} giorni</div>;
  }
  const max = Math.max(...series.map(s => s.usd), 0.000001);
  // Show day labels only every N days when range is wide
  const labelStep = days <= 14 ? 1 : days <= 60 ? Math.ceil(days / 12) : Math.ceil(days / 10);
  return (
    <div>
      {/* Y-axis legend */}
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--text-3)', fontFamily: 'var(--font-mono)', marginBottom: 4 }}>
        <span>$0</span>
        <span>peak: ${max.toFixed(4)}</span>
      </div>
      {/* Bars */}
      <div style={{
        display: 'flex', alignItems: 'flex-end', gap: 2, height: 140,
        background: 'linear-gradient(180deg, rgba(255,255,255,0.02) 0%, transparent 100%)',
        borderTop: '1px solid var(--border)',
        borderBottom: '1px solid var(--border)',
        padding: '4px 0',
      }}>
        {series.map((s, i) => {
          const h = s.usd > 0 ? Math.max(3, (s.usd / max) * 100) : 0;
          const peak = s.usd === max && s.usd > 0;
          return (
            <div key={i}
              title={`${s.day}: $${s.usd.toFixed(4)} • ${s.tokens} tokens • ${s.calls} chiamate`}
              style={{ flex: 1, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', alignItems: 'center', minWidth: 6, position: 'relative' }}
            >
              {peak && (
                <div style={{ position: 'absolute', top: 0, fontSize: 9, color: 'var(--accent2)', fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' }}>
                  ${s.usd.toFixed(4)}
                </div>
              )}
              <div style={{
                width: '90%', height: h + '%',
                background: s.usd > 0
                  ? 'linear-gradient(180deg, var(--accent) 0%, var(--accent-dim) 100%)'
                  : 'transparent',
                borderRadius: '2px 2px 0 0',
                transition: 'height 0.3s',
                opacity: peak ? 1 : 0.85,
                boxShadow: peak ? '0 0 12px var(--accent-glow-strong)' : 'none',
              }} />
            </div>
          );
        })}
      </div>
      {/* X-axis day labels */}
      <div style={{ display: 'flex', gap: 2, marginTop: 4, fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--text-3)' }}>
        {series.map((s, i) => (
          <div key={i} style={{ flex: 1, textAlign: 'center', minWidth: 6, overflow: 'hidden', whiteSpace: 'nowrap' }}>
            {i % labelStep === 0 ? s.label : ''}
          </div>
        ))}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  TAB: EVAL
// ════════════════════════════════════════════════════════════════════════════
function EvalTab() {
  const { t } = useTranslation(['agent']);
  const qc = useQueryClient();
  const { data: list } = useQuery({ queryKey: ['agent-evals'], queryFn: agentApi.evalList });
  const [openId, setOpenId] = useState(null);
  const [showNew, setShowNew] = useState(false);

  const runs = list?.runs || [];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <p style={{ color: 'var(--text-3)', fontSize: 13, margin: 0 }}>{t('eval.subtitle')}</p>
        <Button icon={Plus} onClick={() => setShowNew(true)}>{t('eval.newRun')}</Button>
      </div>

      {runs.length === 0 ? (
        <EmptyState icon={BarChart3} title={t('tabs.eval')} description={t('eval.noRuns')} />
      ) : (
        <Card style={{ padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, fontFamily: 'var(--font-mono)' }}>
            <thead style={{ background: 'var(--bg-2)' }}>
              <tr>
                <th style={th}>{t('eval.name')}</th>
                <th style={th}>Cases</th>
                <th style={th}>{t('eval.faithfulness')}</th>
                <th style={th}>{t('eval.answerRelevancy')}</th>
                <th style={th}>{t('eval.contextPrecision')}</th>
                <th style={th}>Data</th>
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {runs.map(r => (
                <tr key={r.id} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={td}>{r.name}</td>
                  <td style={td}>{r.num_cases}</td>
                  <td style={td}><Score n={r.faithfulness} /></td>
                  <td style={td}><Score n={r.answer_relevancy} /></td>
                  <td style={td}><Score n={r.context_precision} /></td>
                  <td style={td}>{new Date(r.created_at).toLocaleString()}</td>
                  <td style={{ ...td, textAlign: 'right' }}>
                    <button onClick={() => setOpenId(r.id)} style={iconBtn('var(--accent2)')}><Eye size={13} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {showNew && <NewEvalModal onClose={() => { setShowNew(false); qc.invalidateQueries(['agent-evals']); }} />}
      {openId && <EvalDetailModal id={openId} onClose={() => setOpenId(null)} />}
    </>
  );
}

function Score({ n }) {
  if (n == null) return '—';
  const v = parseFloat(n);
  const c = v >= 0.8 ? 'green' : v >= 0.5 ? 'yellow' : 'red';
  return <Badge color={c}>{(v * 100).toFixed(0)}%</Badge>;
}

function NewEvalModal({ onClose }) {
  const { t } = useTranslation(['agent']);
  const [name, setName] = useState('Eval ' + new Date().toLocaleString());
  const [casesText, setCasesText] = useState('');
  const [running, setRunning] = useState(false);

  const run = async () => {
    const cases = casesText.split('\n').map(line => {
      const [q, kws] = line.split('|').map(s => (s || '').trim());
      if (!q) return null;
      return { question: q, expectedKeywords: kws ? kws.split(',').map(s => s.trim()).filter(Boolean) : [] };
    }).filter(Boolean);
    if (cases.length === 0) return toast.error('Aggiungi almeno un caso');
    setRunning(true);
    try {
      await agentApi.evalRun({ name, cases });
      toast.success('Eval completata');
      onClose();
    } catch (err) { toast.error(err.message || 'Eval failed'); }
    setRunning(false);
  };

  return (
    <ModalOverlay onClose={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'var(--bg-2)', border: '1px solid var(--border-bright)', borderRadius: 'var(--radius)',
        padding: 24, width: 'min(760px, 100%)', maxHeight: 'calc(100dvh - 40px)', overflowY: 'auto',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 18 }}>{t('eval.newRun')}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-3)', cursor: 'pointer' }}><X size={18} /></button>
        </div>
        <Input label={t('eval.name')} value={name} onChange={e => setName(e.target.value)} />
        <Input label={t('eval.cases')} value={casesText} onChange={e => setCasesText(e.target.value)} rows={8} placeholder={t('eval.casesPlaceholder')} />
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={run} loading={running} icon={BarChart3}>{t('eval.run')}</Button>
        </div>
      </div>
    </ModalOverlay>
  );
}

function EvalDetailModal({ id, onClose }) {
  const { t } = useTranslation(['agent']);
  const { data, isLoading } = useQuery({ queryKey: ['agent-eval', id], queryFn: () => agentApi.evalGet(id) });

  return (
    <ModalOverlay onClose={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'var(--bg-2)', border: '1px solid var(--border-bright)', borderRadius: 'var(--radius)',
        padding: 24, width: 'min(900px, 100%)', maxHeight: 'calc(100dvh - 40px)', overflowY: 'auto',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 18 }}>{data?.name || 'Eval'}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-3)', cursor: 'pointer' }}><X size={18} /></button>
        </div>
        {isLoading ? <Spinner /> : data && (
          <>
            <div className="grid-stats">
              <StatCard label={t('eval.faithfulness')}     value={`${(data.summary.faithfulness*100).toFixed(0)}%`}      icon={ShieldCheck} color="var(--green)" />
              <StatCard label={t('eval.answerRelevancy')}  value={`${(data.summary.answer_relevancy*100).toFixed(0)}%`}  icon={Brain}       color="var(--accent2)" />
              <StatCard label={t('eval.contextPrecision')} value={`${(data.summary.context_precision*100).toFixed(0)}%`} icon={Hash}        color="var(--accent3)" />
              <StatCard label="Cases" value={data.num_cases} icon={BarChart3} color="var(--accent)" />
            </div>
            <h4 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 13, marginBottom: 8, color: 'var(--text-3)', letterSpacing: '1px', textTransform: 'uppercase' }}>{t('eval.perCase')}</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {(data.results || []).map((c, i) => (
                <div key={i} style={{ padding: 12, background: 'var(--bg-3)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)' }}>
                  <div style={{ fontWeight: 600, color: 'var(--text-1)', marginBottom: 6, fontSize: 12 }}>{c.question}</div>
                  <div style={{ display: 'flex', gap: 6, marginBottom: 6, flexWrap: 'wrap' }}>
                    {c.scores && (
                      <>
                        <Score n={c.scores.faithfulness} />
                        <Score n={c.scores.answer_relevancy} />
                        <Score n={c.scores.context_precision} />
                      </>
                    )}
                    {c.latencyMs && <Badge color="green">{c.latencyMs}ms</Badge>}
                    {c.cost > 0 && <Badge color="purple">${c.cost.toFixed(4)}</Badge>}
                    {c.kwHits?.length > 0 && <Badge color="cyan">{c.kwHits.length} {t('eval.kwHits')}</Badge>}
                    {!c.ok && <Badge color="red">error</Badge>}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-3)', maxHeight: 80, overflowY: 'auto' }}>
                    {c.answer || c.error || '—'}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </ModalOverlay>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  TAB: PERSONAS (vertical agent profiles)
// ════════════════════════════════════════════════════════════════════════════
function PersonasTab() {
  const qc = useQueryClient();
  const currentUser = useStore(s => s.currentUser);
  const isSuperAdmin = currentUser?.role === 'super_admin';
  const { data, isLoading } = useQuery({ queryKey: ['agent-personas-all'], queryFn: () => agentApi.personas() });
  const [editing, setEditing] = useState(null); // persona obj or 'new'

  const delMut = useMutation({
    mutationFn: id => agentApi.personaDelete(id),
    onSuccess: () => { toast.success('Persona eliminata'); qc.invalidateQueries(['agent-personas-all']); qc.invalidateQueries(['agent-personas']); },
  });

  if (isLoading) return <Spinner />;
  const personas = data?.personas || [];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 18 }}>
        <div>
          <p style={{ color: 'var(--text-3)', fontSize: 13, margin: 0 }}>
            Agenti verticali specializzati: ognuno ha system prompt, modelli, tool e nodi di grafo dedicati.
          </p>
          {!isSuperAdmin && (
            <p style={{ color: 'var(--yellow)', fontSize: 11, marginTop: 4 }}>
              ⚠️ Solo super_admin può creare/modificare personas
            </p>
          )}
        </div>
        {isSuperAdmin && (
          <Button icon={Plus} onClick={() => setEditing('new')}>Nuova persona</Button>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 14 }}>
        {personas.map(p => {
          const extraNodes = (p.graphOverrides?.extraNodes || []).length;
          return (
            <Card key={p.id} style={{ padding: 18, position: 'relative', overflow: 'hidden' }}>
              <div style={{
                position: 'absolute', top: -30, right: -30, width: 110, height: 110,
                borderRadius: '50%', background: p.color, opacity: 0.08, filter: 'blur(36px)',
                pointerEvents: 'none',
              }} />
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 10, position: 'relative' }}>
                <div style={{
                  width: 44, height: 44, borderRadius: 12,
                  background: `linear-gradient(135deg, ${p.color}25 0%, ${p.color}08 100%)`,
                  border: `1px solid ${p.color}40`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 22, boxShadow: `0 0 16px ${p.color}25`,
                }}>{p.icon}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 16, color: 'var(--text-1)' }}>{p.name}</div>
                  <code style={{ fontSize: 11, color: p.color }}>{p.slug}</code>
                  {p.isDefault && <Badge color="cyan">default</Badge>}
                </div>
              </div>
              <p style={{ fontSize: 12, color: 'var(--text-2)', lineHeight: 1.5, marginBottom: 12, minHeight: 36 }}>
                {p.description || 'Nessuna descrizione'}
              </p>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
                {p.instanceTag && <Badge color="cyan">tag: {p.instanceTag}</Badge>}
                {p.maxIterations != null && <Badge color="purple">iter: {p.maxIterations}</Badge>}
                {p.citationDeep && <Badge color="green">citation deep</Badge>}
                {p.preferredBrainModel && <Badge>brain: {p.preferredBrainModel}</Badge>}
                {p.preferredSynthModel && <Badge>synth: {p.preferredSynthModel}</Badge>}
                {extraNodes > 0 && <Badge color="yellow">+{extraNodes} node{extraNodes > 1 ? 's' : ''}</Badge>}
              </div>
              {isSuperAdmin && (
                <div style={{ display: 'flex', gap: 6 }}>
                  <button onClick={() => setEditing(p)} style={{
                    flex: 1, padding: '6px 10px', borderRadius: 6, fontSize: 11,
                    background: 'var(--bg-3)', border: '1px solid var(--border)',
                    color: 'var(--text-2)', cursor: 'pointer',
                  }}>✎ Modifica</button>
                  <button
                    onClick={() => { if (window.confirm(`Eliminare "${p.name}"?`)) delMut.mutate(p.id); }}
                    disabled={p.isDefault}
                    style={{
                      padding: '6px 10px', borderRadius: 6, fontSize: 11,
                      background: 'rgba(248,113,113,0.10)', border: '1px solid rgba(248,113,113,0.35)',
                      color: 'var(--red)', cursor: p.isDefault ? 'not-allowed' : 'pointer',
                      opacity: p.isDefault ? 0.4 : 1,
                    }}
                  >✕</button>
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {editing && (
        <PersonaEditor
          persona={editing === 'new' ? null : editing}
          onClose={() => { setEditing(null); qc.invalidateQueries(['agent-personas-all']); qc.invalidateQueries(['agent-personas']); }}
        />
      )}
    </>
  );
}

function PersonaEditor({ persona, onClose }) {
  const isNew = !persona;
  const [form, setForm] = useState(() => persona ? {
    name: persona.name || '',
    slug: persona.slug || '',
    icon: persona.icon || '🤖',
    color: persona.color || '#8b5cf6',
    description: persona.description || '',
    systemPromptTemplate: persona.systemPromptTemplate || '',
    preferredBrainModel: persona.preferredBrainModel || '',
    preferredSynthModel: persona.preferredSynthModel || '',
    maxIterations: persona.maxIterations || '',
    citationDeep: !!persona.citationDeep,
    instanceTag: persona.instanceTag || '',
    graphOverrides: JSON.stringify(persona.graphOverrides || {}, null, 2),
  } : {
    name: '', slug: '', icon: '🤖', color: '#8b5cf6',
    description: '', systemPromptTemplate: '',
    preferredBrainModel: '', preferredSynthModel: '',
    maxIterations: '', citationDeep: false, instanceTag: '',
    graphOverrides: '{}',
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      let overrides = null;
      try { overrides = JSON.parse(form.graphOverrides || '{}'); } catch (e) { toast.error('graphOverrides JSON non valido'); setSaving(false); return; }
      const payload = {
        name: form.name, slug: form.slug, icon: form.icon, color: form.color,
        description: form.description || null,
        systemPromptTemplate: form.systemPromptTemplate || null,
        preferredBrainModel: form.preferredBrainModel || null,
        preferredSynthModel: form.preferredSynthModel || null,
        maxIterations: form.maxIterations ? parseInt(form.maxIterations, 10) : null,
        citationDeep: !!form.citationDeep,
        instanceTag: form.instanceTag || null,
        graphOverrides: overrides,
      };
      if (isNew) await agentApi.personaCreate(payload);
      else       await agentApi.personaUpdate(persona.id, payload);
      toast.success('Persona salvata');
      onClose();
    } catch (err) { toast.error(err.message || 'Errore'); }
    setSaving(false);
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <ModalOverlay onClose={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'var(--bg-2)', border: '1px solid var(--border-bright)',
        borderRadius: 14, padding: 24,
        width: 'min(720px, 100%)', maxHeight: 'calc(100dvh - 40px)', overflowY: 'auto',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 18 }}>
            {isNew ? 'Nuova persona' : `Modifica ${persona.name}`}
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-3)', cursor: 'pointer', fontSize: 20 }}>✕</button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
          <Input label="Nome"  value={form.name} onChange={e => set('name', e.target.value)} />
          <Input label="Slug"  value={form.slug} onChange={e => set('slug', e.target.value)} />
          <Input label="Icona" value={form.icon} onChange={e => set('icon', e.target.value)} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 10 }}>
          <Input label="Colore (hex)" value={form.color} onChange={e => set('color', e.target.value)} />
          <Input label="Tag istanze" value={form.instanceTag} onChange={e => set('instanceTag', e.target.value)} placeholder="es. legale, medical" />
        </div>
        <Input label="Descrizione" value={form.description} onChange={e => set('description', e.target.value)} rows={2} />
        <Input label="System prompt template" value={form.systemPromptTemplate} onChange={e => set('systemPromptTemplate', e.target.value)} rows={5}
          placeholder="Sei un assistente specializzato in…" />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10 }}>
          <Input label="Brain model" value={form.preferredBrainModel} onChange={e => set('preferredBrainModel', e.target.value)} placeholder="gpt-4o-mini" />
          <Input label="Synth model" value={form.preferredSynthModel} onChange={e => set('preferredSynthModel', e.target.value)} placeholder="gpt-4o" />
          <Input label="Max iter" value={form.maxIterations} onChange={e => set('maxIterations', e.target.value)} type="number" />
          <div style={{ paddingTop: 24 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--text-2)', cursor: 'pointer' }}>
              <input type="checkbox" checked={form.citationDeep} onChange={e => set('citationDeep', e.target.checked)} />
              Citation deep
            </label>
          </div>
        </div>
        <div style={{ marginTop: 12 }}>
          <label style={{ display: 'block', fontSize: 11, color: 'var(--text-3)', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: 6 }}>
            Graph overrides (JSON)
          </label>
          <textarea
            value={form.graphOverrides}
            onChange={e => set('graphOverrides', e.target.value)}
            rows={10}
            style={{
              width: '100%', fontFamily: 'var(--font-mono)', fontSize: 11,
              background: 'var(--bg-3)', border: '1px solid var(--border)',
              borderRadius: 6, padding: 10, color: 'var(--text-1)', resize: 'vertical',
            }}
          />
          <div style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 4 }}>
            Esempio: {`{ "forceSettings": {"citationDeep": true}, "extraNodes": [{ "name": "safety", "insertAfter": "synthesizer", "type": "llm_check", "config": {"checkPrompt": "...", "mode": "rewrite_if_unsafe", "disclaimer": "..."}}] }`}
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
          <Button variant="ghost" onClick={onClose}>Annulla</Button>
          <Button onClick={save} loading={saving} icon={CheckCircle2} disabled={!form.name.trim() || !form.slug.trim()}>
            Salva
          </Button>
        </div>
      </div>
    </ModalOverlay>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  STYLES
// ════════════════════════════════════════════════════════════════════════════
const th = {
  padding: '10px 16px', textAlign: 'left',
  fontSize: 10, fontWeight: 600, letterSpacing: '0.5px',
  textTransform: 'uppercase', color: 'var(--text-3)', whiteSpace: 'nowrap',
};
const td = { padding: '10px 16px', color: 'var(--text-1)' };
const iconBtn = (color) => ({
  background: 'transparent', border: 'none', color, cursor: 'pointer', padding: 4,
});
const iconBtnTop = {
  background: 'var(--bg-3)', border: '1px solid var(--border)',
  color: 'var(--text-2)', cursor: 'pointer',
  padding: '4px 8px', borderRadius: 4,
  display: 'inline-flex', alignItems: 'center', gap: 4,
};
